// src/components/portal/EmployeeProfile.js
import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { useAuth } from '../../auth/AuthContext';
import BusinessCentralService from '../../services/businessCentralService';
import { 
  User, Mail, Phone, MapPin, Calendar, Briefcase,
  CreditCard, FileText, Clock, Eye, EyeOff
} from 'lucide-react';

const EmployeeProfile = () => {
  const { user, getAccessToken, activeEnvironment } = useAuth();
  const [employeeData, setEmployeeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleFields, setVisibleFields] = useState({
    bankAccount: false,
    niNumber: false,
    taxCode: false
  });

  // Function to toggle visibility of sensitive fields
  const toggleFieldVisibility = (field) => {
    setVisibleFields({
      ...visibleFields,
      [field]: !visibleFields[field]
    });
  };

  // Function to mask sensitive data
  const maskValue = (value, showLast = 4) => {
    if (!value || value === 'N/A') return 'N/A';
    const length = value.length;
    if (length <= showLast) return '*'.repeat(length);
    return '*'.repeat(length - showLast) + value.slice(-showLast);
  };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return the original string if it's not a valid date
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      logger.error("Error formatting date:", e);
      return dateString;
    }
  };

  // Initialize Business Central service — only recreate when activeEnvironment changes
  const bcService = React.useMemo(() => {
    return new BusinessCentralService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnvironment]);

  // Fetch employee data on component mount
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!user || !activeEnvironment) return;
      
      try {
        setIsLoading(true);
        
        // Fetch user profile from Business Central
        const employeeProfile = await bcService.getEmployeeProfile();
        
        // Update state with employee data
        const fullName = [employeeProfile.firstName, employeeProfile.middleName, employeeProfile.lastName]
          .filter(Boolean) // removes undefined, null, or empty strings
          .join(' ');
        
        setEmployeeData({
          // Personal Information
          name: fullName || user.name,
          firstName: employeeProfile.firstName || '',
          middleName: employeeProfile.middleName || '',
          lastName: employeeProfile.lastName || '',
          birthDate: employeeProfile.birthDate || '',
          gender: employeeProfile.gender || '',
          maritalStatus: employeeProfile.maritalStatus || '',
          
          // Contact Information
          email: employeeProfile.email || user.email,
          phone: employeeProfile.mobilePhoneNo || '',
          address: employeeProfile.address || '',
          address2: employeeProfile.address2 || '',
          addressLine3: employeeProfile.addressLine3 || '',
          addressLine4: employeeProfile.addressLine4 || '',
          city: employeeProfile.city || '',
          county: employeeProfile.county || '',
          postCode: employeeProfile.postCode || '',
          country: employeeProfile.country || '',
          
          // Employment Details
          employeeId: employeeProfile.employeeNo || '',
          position: employeeProfile.jobTitle || 'Employee',
          department: activeEnvironment.department || '',
          manager: employeeProfile.manager || '',
          joinDate: employeeProfile.employmentDate || '',
          status: employeeProfile.status || 'Active',
          hoursWorked: employeeProfile.weeklyHoursWorked || '',
          
          // Tax & NI Information
          taxCode: employeeProfile.hmrcTaxCode || '',
          niNumber: employeeProfile.nationalInsuranceNumber || '',
          niCategory: (employeeProfile.niClassCode || '') + ' ' + (employeeProfile.nationalInsuranceCode || '') || '',
          validP11D: employeeProfile.validP11D || false,
          
          // Payment Information
          bankName: employeeProfile.bankName || '',
          bankAccountName: employeeProfile.bankAccountName || '',
          bankAccountNo: employeeProfile.bankAccountNo || '',
          bankBranchNo: employeeProfile.bankBranchNo || '',
          payFrequency: employeeProfile.frequency || '',
          annualSalary: employeeProfile.annualSalary || '',
          
          // Pension Information
          pensionSet: employeeProfile.pensionSet || '',
          pensionContribution: employeeProfile.pensionContribution || '',
          pensionProvider: employeeProfile.pensionProvider || '',
          
          // Environment info
          environment: activeEnvironment.name,
          environmentType: activeEnvironment.type
        });
        
      } catch (error) {
        logger.error('Error fetching employee data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEmployeeData();
  }, [user, bcService, activeEnvironment]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Employee Profile</h2>
      
      {/* Personal Information Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <User className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Full Name</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.name || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Date of Birth</p>
              <p className="mt-1 text-sm text-gray-900">{formatDate(employeeData?.birthDate)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Gender</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.gender || ''}</p>
            </div>            
          </div>
        </div>
      </div>
      
      {/* Contact Information Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Mail className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Email Address</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.email || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Phone Number</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.phone || ''}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm font-medium text-gray-500">Address</p>
              <p className="mt-1 text-sm text-gray-900">
                {employeeData?.address}
                {employeeData?.address2 && <span>, {employeeData.address2}</span>}
                {employeeData?.addressLine3 && <span><br />{employeeData.addressLine3}</span>}
                {employeeData?.addressLine4 && <span><br />{employeeData.addressLine4}</span>}
                {(employeeData?.city || employeeData?.postCode) && (
                  <span><br />{[employeeData.city, employeeData.postCode].filter(Boolean).join(', ')}</span>
                )}
                {(employeeData?.county || employeeData?.country) && (
                  <span><br />{[employeeData.county, employeeData.country].filter(Boolean).join(', ')}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Employment Details Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Briefcase className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Employment Details</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Employee ID</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.employeeId || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Position</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.position || ''}</p>
            </div>                       
            <div>
              <p className="text-sm font-medium text-gray-500">Start Date</p>
              <p className="mt-1 text-sm text-gray-900">{formatDate(employeeData?.joinDate)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Weekly Hours</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.hoursWorked || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.status || ''}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tax & NI Information Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Tax & National Insurance</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-500">Tax Code</p>
                <button 
                  onClick={() => toggleFieldVisibility('taxCode')} 
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  {visibleFields.taxCode ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-900">
                {visibleFields.taxCode ? 
                  employeeData?.taxCode || '' : 
                  maskValue(employeeData?.taxCode, 2)
                }
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-500">NI Number</p>
                <button 
                  onClick={() => toggleFieldVisibility('niNumber')} 
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  {visibleFields.niNumber ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-900">
                {visibleFields.niNumber ? 
                  employeeData?.niNumber || '' : 
                  maskValue(employeeData?.niNumber, 3)
                }
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">NI Category</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.niCategory || ''}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Banking Details Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <CreditCard className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Banking Details</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Bank Name</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.bankName || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Account Name</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.name || ''}</p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-500">Account Number</p>
                <button 
                  onClick={() => toggleFieldVisibility('bankAccount')} 
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  {visibleFields.bankAccount ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-900">
                {visibleFields.bankAccount ? 
                  employeeData?.bankAccountNo || '' : 
                  maskValue(employeeData?.bankAccountNo, 4)
                }
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Sort Code</p>
              <p className="mt-1 text-sm text-gray-900">
                {visibleFields.bankAccount ? 
                  employeeData?.bankBranchNo || '' : 
                  maskValue(employeeData?.bankBranchNo, 2)
                }
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Frequency</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.payFrequency || ''}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pension Information Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">Pension Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Pension Set</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.pensionSet || ''}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pension Provider</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.pensionProvider || ''}</p>
            </div>   
            <div>
              <p className="text-sm font-medium text-gray-500">Contribution</p>
              <p className="mt-1 text-sm text-gray-900">{employeeData?.pensionContribution || ''}</p>
            </div>         
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;
